import axios from 'axios';

import { API_BASE_URL as API_BASE } from '@/config/apiBaseUrl';

const API_BASE_URL = API_BASE;

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ==================== Shifts ====================
export interface Shift {
  id?: string;
  company_id: string;
  shift_name: string;
  shift_code: string;
  start_time: string;
  end_time: string;
  working_hours: number;
  break_hours: number;
  productive_hours: number;
  working_days: string[];
  is_overtime: boolean;
  overtime_multiplier?: number;
  is_active: boolean;
  shift_sequence: number;
  description?: string;
}

export const shiftService = {
  getAll: (company_id: string) => 
    api.get(`/manufacturing/shifts?company_id=${company_id}`),
  
  getById: (id: string) => 
    api.get(`/manufacturing/shifts/${id}`),
  
  getActive: (company_id: string) => 
    api.get(`/manufacturing/shifts/active?company_id=${company_id}`),
  
  getTotalWorkingHours: (company_id: string, includeOvertime: boolean = false) => 
    api.get(`/manufacturing/shifts/working-hours?company_id=${company_id}&include_overtime=${includeOvertime}`),
  
  create: (data: Shift) => 
    api.post('/manufacturing/shifts', data),
  
  update: (id: string, data: Partial<Shift>) => 
    api.put(`/manufacturing/shifts/${id}`, data),
  
  delete: (id: string) => 
    api.delete(`/manufacturing/shifts/${id}`),
};

// ==================== Production Calendar ====================
export interface CalendarDay {
  id?: string;
  company_id: string;
  date: string;
  day_name: string;
  is_working_day: boolean;
  is_holiday: boolean;
  holiday_type?: 'Public Holiday' | 'Company Holiday' | 'Maintenance Day' | 'Custom';
  holiday_name?: string;
  holiday_description?: string;
  active_shifts?: string[];
  total_available_hours: number;
  total_planned_hours: number;
  capacity_utilization: number;
  work_week?: string;
  work_week_label?: string;
  holiday_calendar?: string | null;
}

export const calendarService = {
  generate: (company_id: string, start_date: string, end_date: string) => 
    api.post('/manufacturing/calendar/generate', { company_id, start_date, end_date }),
  
  getCalendar: (company_id: string, start_date: string, end_date: string) => 
    api.get(`/manufacturing/calendar?company_id=${company_id}&start_date=${start_date}&end_date=${end_date}`),
  
  getWorkingDays: (company_id: string, start_date: string, end_date: string) => 
    api.get(`/manufacturing/calendar/working-days?company_id=${company_id}&start_date=${start_date}&end_date=${end_date}`),
  
  markHoliday: (data: {
    company_id: string;
    date: string;
    holiday_name: string;
    holiday_type: string;
    description?: string;
  }) => 
    api.post('/manufacturing/calendar/holiday', data),
  
  getHolidays: (company_id: string, year: number) => 
    api.get(`/manufacturing/calendar/holidays?company_id=${company_id}&year=${year}`),
  
  delete: (id: string) => 
    api.delete(`/manufacturing/calendar/${id}`),
};

// ==================== Workstations ====================
export interface Workstation {
  id?: string;
  company_id: string;
  workstation_code: string;
  workstation_name: string;
  workstation_type: string;
  location?: string;
  department?: string;
  department_id?: string;
  status: 'Available' | 'In Use' | 'Under Maintenance' | 'Breakdown' | 'Idle';
  capacity_per_day: number;
  efficiency_percentage: number;
  number_of_operators_required: number;
  /** Prefills stage operator when this workstation is selected (Pending stages). */
  default_operator_user_id?: string | null;
  hourly_cost?: number;
  setup_time_hours?: number;
  last_maintenance_date?: string;
  next_maintenance_date?: string;
  maintenance_interval_days: number;
  manufacturer?: string;
  model?: string;
  serial_number?: string;
  purchase_date?: string;
  purchase_cost?: number;
  warranty_months?: number;
  is_active: boolean;
  created_by?: string;
}

export interface WorkstationImportDetailRow {
  row: number;
  workstation_code?: string;
  outcome: 'created' | 'updated' | 'skipped' | 'error';
  message?: string;
}

export interface WorkstationImportSummary {
  created: number;
  updated: number;
  skipped: number;
  failed: number;
  details: WorkstationImportDetailRow[];
}

export const workstationService = {
  getAll: (company_id: string) => 
    api.get(`/manufacturing/workstations?company_id=${company_id}`),
  
  getById: (id: string) => 
    api.get(`/manufacturing/workstations/${id}`),
  
  getAvailable: (company_id: string) => 
    api.get(`/manufacturing/workstations/available?company_id=${company_id}`),
  
  getByDepartment: (company_id: string, department: string) => 
    api.get(`/manufacturing/workstations/department?company_id=${company_id}&department=${department}`),
  
  getMaintenanceDue: (company_id: string) => 
    api.get(`/manufacturing/workstations/maintenance-due?company_id=${company_id}`),
  
  getUtilization: (id: string, start_date: string, end_date: string) => 
    api.get(`/manufacturing/workstations/${id}/utilization?start_date=${start_date}&end_date=${end_date}`),
  
  create: (data: Workstation) => 
    api.post('/manufacturing/workstations', data),
  
  update: (id: string, data: Partial<Workstation>) => 
    api.put(`/manufacturing/workstations/${id}`, data),
  
  updateStatus: (id: string, status: string) => 
    api.put(`/manufacturing/workstations/${id}/status`, { status }),
  
  scheduleMaintenance: (id: string, maintenance_date: string) => 
    api.post(`/manufacturing/workstations/${id}/maintenance`, { maintenance_date }),
  
  delete: (id: string) => 
    api.delete(`/manufacturing/workstations/${id}`),

  downloadImportTemplate: () =>
    api.get('/manufacturing/workstations/import/template', { responseType: 'blob' }),

  importCsv: (file: File, options?: { updateExisting?: boolean }) => {
    const fd = new FormData();
    fd.append('file', file);
    const updateExisting = options?.updateExisting !== false;
    return api.post('/manufacturing/workstations/import', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params: { update_existing: updateExisting ? 'true' : 'false' },
    });
  },
};

// ==================== Capacity Planning ====================
export interface CapacityPlan {
  id?: string;
  company_id: string;
  workstation_id: string;
  shift_id?: string;
  plan_date: string;
  day_of_week: string;
  week_number: number;
  month: number;
  year: number;
  available_capacity_hours: number;
  planned_capacity_hours: number;
  actual_capacity_hours: number;
  overtime_hours: number;
  idle_hours: number;
  downtime_hours: number;
  utilization_percentage: number;
  efficiency_percentage: number;
  oee_percentage: number;
  is_overloaded: boolean;
  is_bottleneck: boolean;
  notes?: string;
}

export const capacityService = {
  generate: (company_id: string, workstation_id: string, start_date: string, end_date: string) =>
    api.post('/manufacturing/capacity/generate', { company_id, workstation_id, start_date, end_date }),
  loadOpenWork: (company_id: string, start_date: string, end_date: string) =>
    api.post('/manufacturing/capacity/load-open-work', { company_id, start_date, end_date }),
  
  getPlan: (company_id: string, workstation_id: string, start_date: string, end_date: string) => 
    api.get(`/manufacturing/capacity?company_id=${company_id}&workstation_id=${workstation_id}&start_date=${start_date}&end_date=${end_date}`),
  
  getSummary: (company_id: string, workstation_id: string, start_date: string, end_date: string) => 
    api.get(`/manufacturing/capacity/summary?company_id=${company_id}&workstation_id=${workstation_id}&start_date=${start_date}&end_date=${end_date}`),
  
  identifyBottlenecks: (company_id: string, start_date: string, end_date: string) => 
    api.get(`/manufacturing/capacity/bottlenecks?company_id=${company_id}&start_date=${start_date}&end_date=${end_date}`),
  
  updatePlanned: (id: string, planned_hours: number) => 
    api.put(`/manufacturing/capacity/${id}/planned`, { planned_hours }),
  
  updateActual: (id: string, actual_hours: number) => 
    api.put(`/manufacturing/capacity/${id}/actual`, { actual_hours }),
};

// ==================== Job Scheduling ====================
export interface JobSchedule {
  id?: string;
  company_id: string;
  work_order_id: string;
  workstation_id: string;
  shift_id?: string;
  schedule_number?: string;
  scheduled_date: string;
  scheduled_start_time?: string;
  scheduled_end_time?: string;
  scheduled_duration_hours: number;
  setup_time_hours?: number;
  actual_start_time?: string;
  actual_end_time?: string;
  actual_duration_hours?: number;
  priority: 'Low' | 'Medium' | 'High' | 'Urgent';
  status: 'Draft' | 'Scheduled' | 'In Progress' | 'Completed' | 'Delayed' | 'Cancelled' | 'Rescheduled';
  due_date?: string;
  planned_quantity: number;
  produced_quantity?: number;
  rejected_quantity?: number;
  rework_quantity?: number;
  is_delayed?: boolean;
  delay_hours?: number;
  delay_reason?: string;
  has_conflicts?: boolean;
  conflict_details?: string;
  notes?: string;
  assigned_to?: string;
  is_auto_scheduled?: boolean;
}

export const scheduleService = {
  getAll: (company_id: string) => 
    api.get(`/manufacturing/schedules?company_id=${company_id}`),
  
  getById: (id: string) => 
    api.get(`/manufacturing/schedules/${id}`),
  
  getByWorkOrder: (work_order_id: string) => 
    api.get(`/manufacturing/schedules/work-order/${work_order_id}`),
  
  getByWorkstation: (workstation_id: string, start_date: string, end_date: string) => 
    api.get(`/manufacturing/schedules/workstation/${workstation_id}?start_date=${start_date}&end_date=${end_date}`),
  
  getSummary: (company_id: string, start_date: string, end_date: string) => 
    api.get(`/manufacturing/schedules/summary?company_id=${company_id}&start_date=${start_date}&end_date=${end_date}`),
  
  detectConflicts: (company_id: string, date: string) => 
    api.get(`/manufacturing/schedules/conflicts?company_id=${company_id}&date=${date}`),
  
  autoSchedule: (company_id: string, work_order_ids: string[]) => 
    api.post('/manufacturing/schedules/auto-schedule', { company_id, work_order_ids }),
  
  create: (data: JobSchedule) => 
    api.post('/manufacturing/schedules', data),
  
  update: (id: string, data: Partial<JobSchedule>) => 
    api.put(`/manufacturing/schedules/${id}`, data),
  
  startJob: (id: string) => 
    api.post(`/manufacturing/schedules/${id}/start`),
  
  completeJob: (
    id: string,
    produced_quantity: number,
    rejected_quantity?: number,
    rework_quantity?: number,
    options?: { finished_serial_or_tag?: string; finished_item_id?: string }
  ) =>
    api.post(`/manufacturing/schedules/${id}/complete`, {
      produced_quantity,
      rejected_quantity,
      rework_quantity,
      finished_serial_or_tag: options?.finished_serial_or_tag,
      finished_item_id: options?.finished_item_id,
    }),
  
  reschedule: (id: string, new_date: string, new_workstation_id?: string) => 
    api.post(`/manufacturing/schedules/${id}/reschedule`, { new_date, new_workstation_id }),
  
  delete: (id: string) => 
    api.delete(`/manufacturing/schedules/${id}`),
};

export default {
  shifts: shiftService,
  calendar: calendarService,
  workstations: workstationService,
  capacity: capacityService,
  schedules: scheduleService,
};

